import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { 
  AccountListItem, 
  AccountFetchFilter, 
  DoubleItem, 
  TripleItem, 
  BalanceSheet, 
  ProfitAndLoss,
  TransactionPagination 
} from '../models';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

class AccountsService {
  // Fetch ledger accounts with token
  async fetchLedgerAccounts(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ accounts: AccountListItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.ledgerList();
      const response = await apiClient.postWithToken<{ accounts: AccountListItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch stock valuation accounts with token
  async fetchStockValuationAccounts(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ accounts: AccountListItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.stockList();
      const response = await apiClient.postWithToken<{ accounts: AccountListItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch trading accounts with token
  async fetchTradingAccounts(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ accounts: AccountListItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.tradingList();
      const response = await apiClient.postWithToken<{ accounts: AccountListItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch ledger details with token
  async fetchLedgerDetail(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: DoubleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.ledger();
      const response = await apiClient.postWithToken<{ items: DoubleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch trading account details with token
  async fetchTradingAccountDetails(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: DoubleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.trading();
      const response = await apiClient.postWithToken<{ items: DoubleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
          error: error instanceof Error ? error.message : 'An unexpected error occurred'
        };
      }
    }

  // Fetch overall trading account details with token
  async fetchOverallTradingAccountDetails(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: DoubleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.tradingOverall();
      const response = await apiClient.postWithToken<{ items: DoubleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch stock valuation details with token
  async fetchStockValuationDetails(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: TripleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.stockValuation();
      const response = await apiClient.postWithToken<{ items: TripleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch overall stock valuation details with token
  async fetchOverallStockValuationDetails(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: TripleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.stockOverall();
      const response = await apiClient.postWithToken<{ items: TripleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch balance sheet with token
  async fetchBalanceSheet(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<BalanceSheet>> {
    try {
      const url = UrlBuilder.accounts.balanceSheet();
      const response = await apiClient.postWithToken<BalanceSheet>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch profit and loss with token
  async fetchProfitAndLoss(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<ProfitAndLoss>> {
    try {
      const url = UrlBuilder.accounts.pnl();
      const response = await apiClient.postWithToken<ProfitAndLoss>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch assets with token
  async fetchAssets(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: DoubleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.assets();
      const response = await apiClient.postWithToken<{ items: DoubleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Fetch liabilities with token
  async fetchLiabilities(token: string, filter?: AccountFetchFilter): Promise<ApiResponse<{ items: DoubleItem[], pagination: TransactionPagination }>> {
    try {
      const url = UrlBuilder.accounts.liabilities();
      const response = await apiClient.postWithToken<{ items: DoubleItem[], pagination: TransactionPagination }>(url, token, filter || {});

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
}

export const accountsService = new AccountsService();